const getStartupAudio = () => {
  if (typeof window === "undefined") return null;
  if (!window.__personalSystemStartupAudio) {
    const audio = new Audio("/static/winxp/sounds/startup.wav");
    audio.preload = "auto";
    audio.playsInline = true;
    window.__personalSystemStartupAudio = audio;
  }
  return window.__personalSystemStartupAudio;
};

export const preloadSystemSound = (name) => {
  if (name !== "startup") return null;
  const audio = getStartupAudio();
  if (audio && !audio.__personalSystemPreloadRequested) {
    audio.__personalSystemPreloadRequested = true;
    audio.load();
  }
  return audio;
};

export const playSystemSound = (name, volume = 0.55) => {
  const audio = name === "startup" ? preloadSystemSound(name) : new Audio(`/static/winxp/sounds/${name}.wav`);
  if (!audio) return null;
  audio.preload = "auto";
  audio.volume = volume;
  if (name === "startup") {
    audio.pause();
    try { audio.currentTime = 0; } catch {}
  }
  window.__personalSystemSound = audio;
  audio.play().catch(() => {});
  return audio;
};

export const gameSound = (src, volume = 0.45) => {
  if (window.__personalSystemMuted) return null;
  try {
    const audio = new Audio(src);
    audio.preload = "auto";
    audio.volume = volume;
    audio.play().catch(() => {});
    return audio;
  } catch {
    return null;
  }
};

export const getPersonalSystemAudioContext = () => {
  if (typeof window === "undefined") return null;
  const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextConstructor) return null;
  if (!window.__personalSystemAudioContext || window.__personalSystemAudioContext.state === "closed") {
    window.__personalSystemAudioContext = new AudioContextConstructor();
  }
  return window.__personalSystemAudioContext;
};

export const resumePersonalSystemAudio = () => {
  const context = getPersonalSystemAudioContext();
  if (context?.state === "suspended") context.resume().catch(() => {});
  return context;
};

export const playPersonalSystemAudioBuffer = (buffer, volume) => {
  const context = getPersonalSystemAudioContext();
  if (!buffer || context?.state !== "running") return false;
  const source = context.createBufferSource(), gain = context.createGain();
  source.buffer = buffer;
  gain.gain.value = volume;
  source.connect(gain);
  gain.connect(context.destination);
  source.start();
  return true;
};

export const playBlackjackWinSound = () => {
  if (window.__personalSystemMuted) return;
  try {
    const audio = window.__personalSystemBlackjackWinAudio ||
      (window.__personalSystemBlackjackWinAudio = new Audio("/static/winxp/sounds/blackjack-cash-register.mp3"));
    audio.preload = "auto";
    audio.volume = 0.48;
    audio.ontimeupdate = () => {
      const trimAt = audio.duration - 0.45;
      if (Number.isFinite(trimAt) && audio.currentTime >= trimAt) {
        audio.pause();
        audio.currentTime = 0;
        audio.ontimeupdate = null;
      }
    };
    audio.currentTime = 0;
    audio.play().catch(() => {});
  } catch {}
};

const CALL_UNAVAILABLE_SOURCE = "/static/winxp/sounds/call-voicemail-clean.wav";
export const CALL_RING_COUNT = 4;
export const CALL_RING_DURATION = 0.882 / 0.82;
export const CALL_MOBILE_AUDIO_BUFFER_MS = 240;

const createCallEndAudio = () => {
  const unavailable = new Audio(CALL_UNAVAILABLE_SOURCE);
  unavailable.preload = "auto";
  unavailable.volume = 0.72;
  return { unavailable };
};

export const stopCallEndAudio = () => {
  const prepared = window.__personalSystemCallEndAudio;
  if (!prepared) return;
  const audio = prepared.unavailable;
  audio.onended = null;
  audio.onerror = null;
  audio.pause();
  audio.currentTime = 0;
  delete window.__personalSystemCallEndAudio;
};

export const prepareCallEndAudio = () => {
  stopCallEndAudio();
  const prepared = createCallEndAudio();
  window.__personalSystemCallEndAudio = prepared;
  return prepared;
};

export const playHangupTone = () => {
  if (window.__personalSystemMuted) return;
  const prepared = window.__personalSystemCallEndAudio || createCallEndAudio();
  window.__personalSystemCallEndAudio = prepared;
  const { unavailable } = prepared;
  if (unavailable.__callEndPlayed) return;
  unavailable.__callEndPlayed = true;
  unavailable.onended = () => { unavailable.onended = null; unavailable.onerror = null; };
  unavailable.onerror = () => { unavailable.onended = null; unavailable.onerror = null; };
  unavailable.pause();
  unavailable.currentTime = 0;
  unavailable.muted = false;
  unavailable.play().catch(() => {});
};

// Link confirmations use the classic Windows XP Error.wav system event.
export const playSystemPing = (volume = 0.4) => playSystemSound("windows-xp-error", volume);

let lastUiClickAt = 0;

export const playUiClick = (volume = 0.16, force = false) => {
  // The generic shell click is a desktop affordance. Keep it off touch-first
  // tablets (including iPadOS with a connected pointer); app/game audio uses
  // separate playback paths and remains available there.
  if (window.matchMedia?.("(pointer: coarse)").matches || window.matchMedia?.("(any-pointer: coarse)").matches) return;
  if (!force && window.matchMedia?.("(max-width: 680px)").matches) return;
  const now = performance.now();
  if (!force && now - lastUiClickAt < 190) return;
  lastUiClickAt = now;
  try {
    const audio = new Audio("/static/winxp/sounds/click.mp3");
    audio.preload = "auto";
    audio.volume = Math.min(0.55, volume * 2.4);
    audio.playbackRate = 1.08;
    audio.play().catch(() => {});
  } catch {}
};

// Window transitions use the existing retro click only, without a second beep.
export const playWindowTone = () => {};
